// @ts-nocheck

import * as Sentry from '@sentry/react';
import { has, get, reduce, intersection, uniq } from 'lodash';

import {
  userActionCreators,
  basketActionCreators,
  entitiesActionCreators,
  favoritesActionCreators,
  flashMessageActionCreators,
} from '../actions';
import { setRealityCheckMessage } from '../actions/regulation';
import { setDidReceivedUserData } from '../actions/user';
import config from '../config/app.config';
import { __ } from '../i18n/l10n';
import { updateBoomUserState } from '../modules/boom/actions/boom';
import { updateGRUserState } from '../modules/gift-race/actions/gr';
import {
  setHistoryReferences as setGridHistoryReferences,
  setHistoryGridCount as setGridHistoryGridCount,
} from '../modules/grids/actions/history';
import { setActivePools, setRunningGrids } from '../modules/grids/actions/ui';
import immersiveActions from '../modules/immersive/actions';
import { updateKLUserState } from '../modules/koh-lanta/actions/kl';
import { setUserMissionsModerator, setUserPromotionStatus } from '../modules/missions/actions/user';
import { updateRegisteredMissions, updateUserMissionsState } from '../modules/missions/actions/userMissions';
import { updateMPUserState } from '../modules/monopoly/actions/mp';
import BetBuilder from '../states/betbuilder';
import { BetslipErrorCodes } from '../types/basket';
import { FlashMessageTypes, FlashMessageSeverities, FlashMessageScopes } from '../types/flashmessage';

const { version } = config;

const dispatcher = ({ dispatch, getState }, message, history) => {
  if (typeof message === 'object' && message !== null) {
    // --------- INITIAL STATE ONLY

    // Do we have to prompt the user to validate the terms and conditions of use
    if (message.terms === true) {
      dispatch(userActionCreators.setMustValidateCGU());
    }

    // Do we have to prompt the user to validate some events (arjel, etc..)
    if (message.events === true) {
      dispatch(userActionCreators.setMustValidateEvents());
    }

    // User account related notification
    if (Array.isArray(message.notifications) && message.notifications.length > 0) {
      dispatch(userActionCreators.setMessages(message.notifications));
    }

    // --------- REGULAR

    if (has(message, 'getGrids') === true) {
      const { gridReferences, totalGrids } = get(message, 'getGrids', {});
      dispatch(setGridHistoryReferences(gridReferences));
      dispatch(setGridHistoryGridCount(totalGrids));
    }

    if (has(message, 'activePools') === true) {
      const activePools = get(message, 'activePools', {});
      dispatch(setActivePools(activePools));
    }

    if (has(message, 'runningGrids') === true) {
      const activePools = get(message, 'runningGrids', {});
      dispatch(setRunningGrids(activePools));
    }

    // Show modal to refresh to current page
    if (has(message, 'version') === true) {
      const nextVersion = get(message, 'version', {});
      if (version !== nextVersion) {
        window && typeof window.showOutdatedClientModal === 'function' && window.showOutdatedClientModal();
      }
    }

    // Dispatch last session settled bets
    if (has(message, 'cc.lastSessionSettledBets')) {
      const lastSessionSettledBets = get(message, 'cc.lastSessionSettledBets', []);
      dispatch(userActionCreators.setLastSessionSettledBets(uniq(lastSessionSettledBets)));
    }

    // Dispatch bankroll type, PM / RM
    if (has(message, 'cc.balance.type')) {
      const isRealMoney = get(message, 'cc.balance.type', 'real');
      dispatch(userActionCreators.setRealMoney(isRealMoney === 'real'));
    }

    // Dispatch user nickname
    if (has(message, 'cc.user.nickname')) {
      const nickname = get(message, 'cc.user.nickname', undefined);
      // Set a tag in sentry for nickname
      Sentry.setTag('nickname', nickname);
      dispatch(userActionCreators.setNickname(nickname));
    }

    // Dispatch bankroll
    if (
      has(message, 'cc.balance.amount') ||
      has(message, 'cc.balance.amountCash') ||
      has(message, 'cc.balance.amountBonus') ||
      has(message, 'cc.freebetBankroll') ||
      has(message, 'cc.balanceChange')
    ) {
      // Despite his property name, it can be the real or play balance
      const balance = get(message, 'cc.balance.amount', undefined);
      const balanceCash = get(message, 'cc.balance.amountCash', undefined);
      const balanceBonus = get(message, 'cc.balance.amountBonus', undefined);
      const freebetBankroll = get(message, 'cc.freebetBankroll', undefined);
      const origin = get(message, 'cc.balanceChange.origin', undefined);
      const type = get(message, 'cc.balanceChange.type', undefined);

      dispatch(userActionCreators.setBalance({ balance, balanceCash, balanceBonus, freebetBankroll, origin, type }));
    }

    // Dispatch last amount betted in cents
    if (has(message, 'cc.lastAmount')) {
      const lastAmountBetted = get(message, 'cc.lastAmount', 0);
      if (lastAmountBetted > 0) {
        dispatch(basketActionCreators.basketSetDefaultStake(lastAmountBetted / 100));
        dispatch(immersiveActions.setDefaultStake(lastAmountBetted / 100));
      }
    }

    // Dispatch presets
    if (has(message, 'cc.presets')) {
      const presets = get(message, 'cc.presets', undefined);
      dispatch(userActionCreators.setPresets(presets));
    }

    // Dispatch freebets
    if (has(message, 'cc.freebets')) {
      const freebets = get(message, 'cc.freebets', []);
      if (Array.isArray(freebets)) {
        dispatch(basketActionCreators.basketReceiveFreebets(freebets));
      }
    }

    // Dispatch boosters
    if (has(message, 'cc.boosters')) {
      const boosters = get(message, 'cc.boosters', []);
      if (Array.isArray(boosters)) {
        dispatch(basketActionCreators.basketReceiveBoosters(boosters));
      }
    }

    // Dispatch bonuses
    if (has(message, 'cc.promotions')) {
      const bonuses = get(message, 'cc.promotions', []);
      if (Array.isArray(bonuses)) {
        dispatch(basketActionCreators.basketReceiveBonuses(bonuses));
      }
    }

    // Dispatch favorites
    if (has(message, 'favorites.matches')) {
      const favorites = get(message, 'favorites.matches', []);
      if (Array.isArray(favorites)) {
        dispatch(favoritesActionCreators.setFavorites(favorites));
      }
    }

    // Dispatch favorites response
    if (has(message, 'favoritesResult')) {
      const favoritesResponse = get(message, 'favoritesResult', {});
      dispatch(favoritesActionCreators.receiveResponse(favoritesResponse));
    }

    // Dispatch notification
    // Mainly used for notify the user that his bet has been validated / refused
    // TODO: refactor this part and this related reducer !!
    if (has(message, 'cc.notification')) {
      const notification = get(message, 'cc.notification', {});
      if (notification) {
        const { betslipAccepted, betslipReference, traderID: traderId, betslip } = notification;
        let estr = undefined;
        if (betslipAccepted) {
          // Not sure if this is still useful
          if (window && window.showArjelEventModal) window.showArjelEventModal();

          const betslipId = betslipReference.split('-')[1];
          estr = betslip.AM
            ? `${__('Pending bet validated')} ${betslipId} - ${betslip.AM}`
            : `${__('Pending bet validated')} ${betslipId}`;
          dispatch(
            basketActionCreators.basketSetNotification({
              traderId,
              betslipId: betslipReference,
              error: {
                ecode: 0,
                estr,
              },
              additionalMessage: betslip.AM,
            }),
          );
          dispatch(
            immersiveActions.onTraderNotification(`${__('Bet validated')} - ${betslipId}`, traderId, betslipAccepted),
          );

          const { B, AM: additionalMessage } = betslip;
          const betList = B.map(({ BID, O, R }) => ({ bid: BID, o: O, r: R }));
          dispatch(
            basketActionCreators.basketValidateItems({
              betList,
              betslipId: betslipReference,
              additionalMessage,
              traderId,
            }),
          );
        } else {
          estr = __('Pending bet refused');
          dispatch(
            basketActionCreators.basketSetNotification({
              traderId,
              error: {
                ecode: BetslipErrorCodes.TRADER,
                estr,
              },
              additionalMessage: betslip?.AM,
            }),
          );
          dispatch(immersiveActions.onTraderNotification(__('Bet refused'), traderId, betslipAccepted));
        }
        dispatch(basketActionCreators.basketSetSelection());

        if (history?.location?.hash?.indexOf('#cart') === -1) {
          dispatch(
            flashMessageActionCreators.addMessage({
              scope: FlashMessageScopes.BASKET,
              severity: notification.betslipAccepted ? FlashMessageSeverities.NOTICE : FlashMessageSeverities.ERROR,
              type: FlashMessageTypes.DISMISS_MANUAL,
              message: estr,
            }),
          );
        }
      }
    }

    // Dispatch affiliation amount
    if (has(message, 'cc.godfather') && has(message, 'cc.godson')) {
      const {
        cc: { godfather: godfatherAmount, godson: godsonAmount },
      } = message;
      dispatch(userActionCreators.setAffiliation({ godfatherAmount, godsonAmount }));
    }

    if (has(message, 'cc.kl')) {
      const {
        cc: { kl: userStateMutation },
      } = message;
      dispatch(updateKLUserState({ userStateMutation }));
    }

    if (has(message, 'cc.gr')) {
      const {
        cc: { gr: userStateMutation },
      } = message;
      dispatch(updateGRUserState({ userStateMutation }));
    }

    if (has(message, 'cc.monopoly')) {
      const {
        cc: { monopoly: userStateMutation },
      } = message;
      dispatch(updateMPUserState({ userStateMutation }));
    }

    if (has(message, 'cc.boom')) {
      const {
        cc: { boom: userStateMutation },
      } = message;
      dispatch(updateBoomUserState({ userStateMutation }));
    }

    if (has(message, 'cc.userMissions')) {
      const {
        cc: { userMissions: userMissionsMutation },
      } = message;
      dispatch(updateUserMissionsState({ userMissionsMutation }));
    }

    if (has(message, 'cc.registeredMissions')) {
      const {
        cc: { registeredMissions: registeredMissionsMutation },
      } = message;
      dispatch(updateRegisteredMissions({ registeredMissionsMutation }));
    }

    if (has(message, 'cc.missionsModerator')) {
      const {
        cc: { missionsModerator },
      } = message;
      dispatch(setUserMissionsModerator({ missionsModerator }));
    }

    if (has(message, 'cc.promotionStatus')) {
      const {
        cc: { promotionStatus },
      } = message;
      dispatch(setUserPromotionStatus({ promotionStatus }));
    }

    if (has(message, 'cc.realityCheck')) {
      const {
        cc: { realityCheck },
      } = message;
      dispatch(setRealityCheckMessage({ message: realityCheck }));
    }

    // Dispatch entities
    dispatch(entitiesActionCreators.receiveEntities(message));

    // --------- TODO REFACTOR

    // To handle in basket reducer
    const { basket, entities } = getState();
    // Specific case for basket !
    if (basket.loading === true) {
      const { matches, bets, outcomes } = entities;
      const { items } = basket;
      const isBasketItemsLoaded = reduce(
        items,
        (isLoaded, { oddId, matchId, betId, betbuilder, selectionItems }) => {
          if (isLoaded) {
            if (betbuilder) {
              return (
                matches[matchId] !== undefined &&
                !!selectionItems &&
                BetBuilder.isItemsOutcomesAndBetsStored(selectionItems, bets, outcomes)
              );
            }

            return matches[matchId] !== undefined && bets[betId] !== undefined && outcomes[oddId] !== undefined;
          }
          return false;
        },
        true,
      );

      // if true, allows the winamax loader to disappear and the saved basket items to be displayed
      if (isBasketItemsLoaded) {
        console.debug(`%c basket | items loaded `, 'background: #616161; color: #fff;');
        setTimeout(() => {
          dispatch(basketActionCreators.basketSetLoadingOk());
        }, 200);
      }
    }

    // Same here, should be handled by the reducer
    if (has(message, 'odds') === true) {
      const oddsKeys = Object.keys(message.odds || []);
      const itemsKeys = Object.keys(basket.items);
      const interKeys = intersection(oddsKeys, itemsKeys);
      if (interKeys.length > 0) {
        dispatch(basketActionCreators.basketRefresh({ force: false }));
      }
    }

    if (has(message, 'cc')) {
      const { runtime } = getState();
      const { isWebsocketConnected } = runtime;

      if (isWebsocketConnected) {
        dispatch(setDidReceivedUserData(true));
      }
    }
  }
};

export default dispatcher;
